import type { Profile } from '@models/session';

import { type RoleModel, RoleName } from 'generated/types';

type UserWithRoles = Pick<Profile, 'account_id'> & {
  roles?: Pick<RoleModel, 'role_name'>[] | null;
};

const hasRole = (user: UserWithRoles, role_name: RoleName) => {
  const roleNames = (user.roles ?? []).map(({ role_name }) => role_name);
  return roleNames.includes(role_name);
};

const isPatient = (user: UserWithRoles) => hasRole(user, RoleName.Patient);
const isUser = (user: UserWithRoles) => hasRole(user, RoleName.User);
const isClient = (user: UserWithRoles) => hasRole(user, RoleName.Client);
const isVitallAdmin = (user: UserWithRoles) => hasRole(user, RoleName.Admin);
const isAccountAdmin = (user: UserWithRoles) =>
  hasRole(user, RoleName.AccountAdmin);
const isPractitioner = (user: UserWithRoles) =>
  hasRole(user, RoleName.Practitioner);
const isDependant = (user: UserWithRoles) => isPatient(user) && !isUser(user);

const displayUserRoles = (user: UserWithRoles, account_id: string) => {
  if (isClient(user)) {
    return [
      `${isPatient(user) ? 'GLOBAL.patient' : 'GLOBAL.nonPatient'}`,
      `${
        account_id === user.account_id ? 'GLOBAL.member' : 'GLOBAL.nonMember'
      }`,
    ];
  }
  return [
    ...(isPractitioner(user) ? ['GLOBAL.practitioner'] : []),
    'GLOBAL.nonMember',
  ];
};

export {
  displayUserRoles,
  isAccountAdmin,
  isVitallAdmin,
  isClient,
  isDependant,
  isPatient,
  isPractitioner,
  isUser,
  hasRole,
};
